/**
 * The folder trail above the documents table (#6559): the drive root, then one
 * crumb per folder segment. Every crumb but the last is a button that climbs
 * back to that folder; the last is the folder the table lists.
 */
import React from "react";
import { Tooltip } from "@mui/material";
import { CaretRightIcon } from "@phosphor-icons/react";

interface BreadcrumbsProps {
  path: string[];
  rootLabel: string;
  onNavigate: (path: string[]) => void;
  /** False when the trail sits above a heading that names the place: every
   *  crumb, the last included, is then a step back. */
  current?: boolean;
  /** Drawn before the last crumb's label: a record crumb carries the
   *  record's icon (Claude Design, round 10). */
  lastIcon?: React.ReactNode;
  /** The place the trail starts at (round 17): a connection's root folder.
   *  Crumbs are drawn for the segments after it, the root crumb is the
   *  place, and the trail never climbs above it. */
  base?: string[];
  /** Leave the root crumb out (round 18h): the picker's location chip is
   *  the root, so the trail shows only the folders beneath the place. */
  hideRoot?: boolean;
}

const crumbBase =
  "max-w-[220px] rounded-[6px] px-1.5 py-0.5 text-[13px] font-medium";
const crumbClass = `${crumbBase} truncate`;
/** The current crumb: a flex row so an icon sits on the label's line, with
 *  the ellipsis on the label's own box (a flex container cannot ellipsize
 *  its text; round 10 review, round 11). */
const currentCrumbClass = `${crumbBase} inline-flex items-center gap-1.5 text-[var(--fp-ink)]`;

const Breadcrumbs: React.FC<BreadcrumbsProps> = ({
  path,
  rootLabel,
  onNavigate,
  current = true,
  lastIcon,
  base = [],
  hideRoot = false,
}) => {
  const relative = path.slice(base.length);
  const crumbs = hideRoot ? relative : [rootLabel, ...relative];
  // With the root left out, crumb i is folder i+1 of the relative path.
  const offset = hideRoot ? 1 : 0;
  return (
    <nav
      aria-label="Folder path"
      className="flex min-w-0 items-center gap-0.5 text-[var(--fp-ink-2)]"
    >
      {crumbs.map((label, index) => {
        const isLast = current && index === crumbs.length - 1;
        const target = [...base, ...relative.slice(0, index + offset)];
        return (
          <React.Fragment key={`${index}:${label}`}>
            {index > 0 && (
              <CaretRightIcon
                size={12}
                aria-hidden
                className="shrink-0 text-[var(--fp-ink-3)]"
              />
            )}
            {isLast ? (
              <Tooltip title={label} placement="bottom" enterDelay={600}>
                <span className={currentCrumbClass}>
                  {lastIcon}
                  {/* The label carries the current mark: it is the crumb's
                      text, and what a reader of the trail finds by it. */}
                  <span aria-current="location" className="min-w-0 truncate">
                    {label}
                  </span>
                </span>
              </Tooltip>
            ) : (
              <Tooltip title={label} placement="bottom" enterDelay={600}>
                <button
                  type="button"
                  onClick={() => onNavigate(target)}
                  className={`${crumbClass} hover:bg-[var(--fp-ink-wash)] hover:text-[var(--fp-ink)]`}
                >
                  {label}
                </button>
              </Tooltip>
            )}
          </React.Fragment>
        );
      })}
    </nav>
  );
};

export default Breadcrumbs;
